/**
 * Pure, import-safe security helpers for the playground dev-server plugin.
 *
 * Kept free of `import.meta` / Node-only imports so they can be unit-tested
 * under ts-jest (the plugin itself uses `import.meta.url` and cannot be imported
 * in jsdom tests).
 */

import type { PlaygroundTarget } from '../config/playground'

const VALID_TARGETS = new Set(['local', 'dev', 'staging', 'playground'])

/**
 * Strict allowlist for the `target` used to build a `game.config.<target>.json`
 * path. Blocks path traversal / separators / unknown values that could let a
 * dev-server request write outside the project root.
 */
export function isSafeConfigTarget(target: unknown): target is string {
  return typeof target === 'string' && VALID_TARGETS.has(target)
}

/** A plain id with no path separators — used to validate `gameId` before writing. */
export function isSafeGameId(gameId: unknown): gameId is string {
  return typeof gameId === 'string' && gameId.length > 0 && gameId.length <= 128 && /^[A-Za-z0-9_-]+$/.test(gameId)
}

/**
 * Exact-match (case-insensitive) Host-header allowlist. Defends against
 * DNS-rebinding: a malicious page resolving its own domain to 127.0.0.1 still
 * sends its own Host, which is not in the allowlist.
 */
export function isAllowedHost(host: string | undefined, allowed: string[]): boolean {
  if (!host) return false
  const normalized = host.toLowerCase()
  return allowed.some((a) => a.toLowerCase() === normalized)
}

const LOOPBACK_HOSTS = new Set(['127.0.0.1', '::1', '[::1]', 'localhost'])

/**
 * True when the resolved bind host is loopback (or unset — Vite's default bind
 * is loopback). A non-loopback bind (`0.0.0.0`, a LAN IP) exposes the dev server
 * to the network.
 */
export function isLoopbackHost(host: string | undefined): boolean {
  if (host === undefined || host === '') return true
  return LOOPBACK_HOSTS.has(host.toLowerCase())
}

/**
 * True for the dev-server middleware endpoints (`/__rundot-*`). The Vite proxies
 * (`/__rundotgameapi`, `/__rundotcloudrun`, `/__rundotgamemanager` — no hyphen)
 * and the init script (`/@rundot-…`) intentionally do NOT match.
 */
export function isProtectedRundotPath(url: string): boolean {
  return url.startsWith('/__rundot-')
}

/** Extract the hostname from a Host header, dropping the port and IPv6 brackets. */
export function hostnameOf(hostHeader: string | undefined): string | undefined {
  if (!hostHeader) return undefined
  const h = hostHeader.trim()
  if (h.startsWith('[')) {
    const end = h.indexOf(']')
    return end > 0 ? h.slice(1, end).toLowerCase() : undefined
  }
  const colon = h.indexOf(':')
  return (colon >= 0 ? h.slice(0, colon) : h).toLowerCase()
}

/**
 * Combined DNS-rebinding + CSRF guard decision for a `/__rundot-*` request.
 * Passes only when the Host hostname is allowlisted AND the request carries a
 * signal a cross-origin *simple* request cannot forge (our custom header, or a
 * same-origin `Sec-Fetch-Site`). Default-deny.
 */
export function isPlaygroundRequestAllowed(params: {
  host: string | string[] | undefined
  allowedHostnames: string[]
  /**
   * When the dev server intentionally binds all interfaces (`0.0.0.0`/`::`, e.g.
   * `vite --host` for LAN/mobile testing), skip the Host-rebinding allowlist —
   * LAN clients legitimately send a non-loopback Host. The CSRF signal below is
   * still required, and a headless key + non-loopback bind is refused at startup,
   * so this does not weaken the token-oracle defense.
   */
  allowAnyHost?: boolean
  playgroundHeader: string | string[] | undefined
  secFetchSite: string | string[] | undefined
}): boolean {
  if (!params.allowAnyHost) {
    const host = typeof params.host === 'string' ? params.host : undefined
    if (!isAllowedHost(hostnameOf(host), params.allowedHostnames)) return false
  }
  const hasCustomHeader = params.playgroundHeader === '1'
  const sameOrigin = params.secFetchSite === 'same-origin'
  return hasCustomHeader || sameOrigin
}

/**
 * Whether the dev server should advertise the anonymous-sign-in fallback for
 * LAN/mobile playground testing. Google OAuth can't run from a non-`localhost`
 * origin, so without this a phone hitting the LAN IP hangs on "signing in".
 *
 * Gated to a local `vite serve`, the `playground` target, a non-loopback bind
 * (the LAN-testing signal), and NO headless auth (a headless key drives its own
 * sign-in and, on a non-loopback bind, already fails fast at startup). Never
 * true for a production build, so the hosted playground keeps Google mandatory.
 */
export function shouldAllowLanAnonymousAuth(params: {
  isServe: boolean
  target: PlaygroundTarget
  bindHost: string | undefined
  hasHeadlessAuth: boolean
}): boolean {
  return (
    params.isServe &&
    params.target === 'playground' &&
    !isLoopbackHost(params.bindHost) &&
    !params.hasHeadlessAuth
  )
}

/**
 * Whether the dev server should perform a headless key exchange for this key.
 * True only for a `pk_` (playground) key on a playground target, or a non-`pk_`
 * (internal QA) key when explicitly opted in. An `rk_` deploy key is NOT
 * eligible (use `rundot playground grant-access` to mint a `pk_` key). The raw
 * key is never exposed to the browser regardless — this only gates the boolean
 * flag and the exchange middleware.
 */
export function shouldExposeHeadlessAuth(
  key: string | undefined,
  target: PlaygroundTarget,
  allowNonRkKey: boolean,
): boolean {
  if (!key) return false
  if (target !== 'playground') return false
  if (key.startsWith('pk_')) return true
  return allowNonRkKey
}
